import { useEffect, useRef } from "react";
import { requestIdle } from "./requestIdleCallbackPolyfill";

/**
 * useInViewAnimation
 * Safely observes elements and applies an attribute / class when they enter view.
 * Defensive:
 * - No DOM access on server.
 * - Graceful fallback if IntersectionObserver unsupported.
 * - Cleans up observers and scheduled idle tasks.
 *
 * Parameters (opts):
 *  root, rootMargin, threshold        -> IntersectionObserver config
 *  once (boolean)                     -> unobserve after first intersection
 *  attribute (string)                 -> attribute name to set (default data-animated)
 *  attributeValue (string)            -> value for attribute (default "true")
 *  animationClass (string)            -> optional class to add when revealed
 */
export default function useInViewAnimation(
  selector = ".feature-card",
  opts = {}
) {
  const optsRef = useRef(opts);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const elements = Array.from(document.querySelectorAll(selector));
    if (!elements.length) return;

    // Fallback: no IntersectionObserver support
    if (typeof window.IntersectionObserver !== "function") {
      elements.forEach((el) => {
        el.setAttribute(
          optsRef.current.attribute || "data-animated",
          optsRef.current.attributeValue || "true"
        );
        if (optsRef.current.animationClass) {
          el.classList.add(optsRef.current.animationClass);
        }
      });
      return;
    }

    const {
      root = null,
      rootMargin = "0px",
      threshold = 0.2,
      once = true,
      attribute = "data-animated",
      attributeValue = "true",
      animationClass = "",
    } = optsRef.current;

    let cancelled = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const target = entry.target;
            try {
              if (attribute) target.setAttribute(attribute, attributeValue);
              if (animationClass) target.classList.add(animationClass);
            } catch (e) {
              console.warn("Animation attribute/class apply failed:", e);
            }
            if (once) observer.unobserve(target);
        });
      },
      { root, rootMargin, threshold }
    );

    // Stagger observation using idle callback (fallback to setTimeout)
    const idleHandles = elements.map((el, idx) =>
      requestIdle(() => {
        if (!cancelled) observer.observe(el);
      }, 40 + idx * 25)
    );

    return () => {
      cancelled = true;
      try { observer.disconnect(); } catch {}
      idleHandles.forEach((h) => clearTimeout(h));
    };
  }, [selector]);
}